import FontAwesomeIcon from 'src/components/Icon';
import React, { ReactElement } from 'react';

export interface EventTypeStyle {
  backgroundColor: string;
  textColor: string;
  label: string;
}

export interface EventTypeStyles {
  walkthrough: EventTypeStyle;
  service: EventTypeStyle;
  'key-pickup': EventTypeStyle;
}

export type EventType = keyof EventTypeStyles;

export const PROJECT_COLORS: string[] = [
  // Blues
  '#2563eb', // Strong blue
  '#3b82f6', // Blue-500
  '#1d4ed8', // Blue-700
  '#0891b2', // Strong teal
  '#06b6d4', // Cyan-500
  '#0e7490', // Cyan-700

  // Reds/Pinks
  '#dc2626', // Strong red
  '#ef4444', // Red-500
  '#b91c1c', // Red-700
  '#ec4899', // Strong pink
  '#f43f5e', // Rose-500
  '#d946ef', // Fuchsia-500

  // Greens
  '#059669', // Strong green
  '#22c55e', // Green-500
  '#15803d', // Green-600
  '#84cc16', // Strong lime
  '#65a30d', // Lime-600
  '#16a34a', // Green-500 alt

  // Oranges/Yellows
  '#d97706', // Strong orange
  '#f97316', // Orange-500
  '#ea580c', // Orange-600
  '#f59e0b', // Strong yellow
  '#eab308', // Yellow-500
  '#ca8a04', // Yellow-600

  // Purples/Violets
  '#7c3aed', // Strong purple
  '#8b5cf6', // Violet-500
  '#7c2d12', // Violet-700
  '#a855f7', // Purple-500
  '#9333ea', // Purple-600
  '#6b21a8', // Purple-700

  // Neutrals/Browns
  '#374151', // Strong gray
  '#b45309', // Strong amber
  '#64748b', // Slate-500
  '#78716c', // Stone-500
  '#525252', // Neutral-500
  '#92400e', // Amber-700
];

// Event type styling constants.
export const EVENT_TYPE_STYLES = {
  walkthrough: {
    backgroundColor: '#dbdfe5', // Light gray.
    textColor: '#2d3748', // Dark text.
    label: 'Walkthrough',
  },
  service: {
    backgroundColor: '#51575e', // Darker gray.
    textColor: 'white', // Light text.
    label: 'Service Request',
  },
  'key-pickup': {
    backgroundColor: '#000000', // Black — distinct from walkthrough (light) and service (dark gray).
    textColor: 'white',
    label: 'Key Pickup',
  },
} as const;

export const getEventTypeStyle = (eventType: string): EventTypeStyle => {
  return EVENT_TYPE_STYLES[eventType as EventType] || EVENT_TYPE_STYLES.service;
};

export const getPropertyColorIndex = (
  propertyName: string,
  paletteSize: number = PROJECT_COLORS.length
): number => {
  if (!propertyName) return 0;

  // DJB2 hash - much better distribution.
  let hash: number = 5381;

  for (let i = 0; i < propertyName.length; i++) {
    hash = (hash * 33) ^ propertyName.charCodeAt(i);
  }

  return Math.abs(hash) % paletteSize;
};

export const getProjectColor = (propertyName: string): string => {
  return PROJECT_COLORS[getPropertyColorIndex(propertyName)];
};

export const getEventIcon = (eventType: string): ReactElement | null => {
  switch (eventType) {
    case 'walkthrough':
      return <FontAwesomeIcon glyph="walking" weight="regular" size="small" />;
    case 'service':
      return <FontAwesomeIcon glyph="wrench" weight="regular" size="small" />;
    case 'key-pickup':
      return <FontAwesomeIcon glyph="key" weight="regular" size="small" />;
    default:
      return null;
  }
};
